import { Header, Main } from '@pcrm/fe/ui-kit';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { currentBillingMonth } from '../../../features/billing/billing.api';
import {
  BILLING_STATE_FILTERS,
  BillingScreen,
  type BillingStateFilter,
} from '../../../features/billing/billing-screen';

const stateValues = BILLING_STATE_FILTERS.map((filter) => filter.value) as [
  BillingStateFilter,
  ...BillingStateFilter[],
];

/**
 * Luna, firma și filtrul de stare stau în adresă, nu în starea componentei: un link trimis
 * cuiva deschide **aceeași listă** (#184), iar un refresh nu aduce ecranul înapoi pe luna
 * curentă după ce omul a ales septembrie.
 *
 * O valoare stricată în adresă cade pe implicit, nu dă eroare: `luna=2026-13` deschide luna
 * curentă, nu un ecran alb.
 */
const searchSchema = z.object({
  luna: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
  firma: z.uuid().optional().catch(undefined),
  stare: z.enum(stateValues).optional().catch(undefined),
});

/**
 * „Conta → Facturare" ([#184](https://github.com/bplugaru/efx-pcrm/issues/184)).
 *
 * `Header` și `Main` stau în pagină, ca la Parteneri: zona Conta n-are încă un layout propriu.
 */
export const Route = createFileRoute('/_authenticated/conta/facturare')({
  validateSearch: searchSchema,
  component: FacturarePagina,
});

function FacturarePagina() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Luna implicită e calculată la București, la deschidere — doar un punct de plecare.
  const month = search.luna ?? currentBillingMonth();
  const go = (patch: Partial<z.infer<typeof searchSchema>>) =>
    // `replace`: schimbarea unui filtru nu e un pas în istoric.
    navigate({ search: (current) => ({ ...current, ...patch }), replace: true });

  return (
    <>
      <Header fixed>
        <h1 className="text-lg font-semibold">Conta</h1>
      </Header>

      <Main className="flex flex-1 flex-col gap-4 sm:gap-6">
        <BillingScreen
          month={month}
          companyId={search.firma}
          stateFilter={search.stare}
          onMonthChange={(luna) => go({ luna })}
          onCompanyChange={(firma) => go({ firma })}
          onStateFilterChange={(stare) => go({ stare })}
        />
      </Main>
    </>
  );
}
